
import React from 'react';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ShareOptionsSection } from "./form-sections/ShareOptionsSection";
import { CustomMessagesSection } from "./form-sections/CustomMessagesSection";
import { RewardConfigSection } from "./form-sections/RewardConfigSection";
import { AdditionalSettingsSection } from "./form-sections/AdditionalSettingsSection";
import { AffiliateSettingsSection } from "./form-sections/AffiliateSettingsSection";
import { MilestoneThresholdsSection } from "./form-sections/MilestoneThresholdsSection";
import { NewsletterReferralSection } from "./form-sections/NewsletterReferralSection";
import { LeaderboardSettingsSection } from "./form-sections/LeaderboardSettingsSection";
import type { CampaignSettings } from "./types";

interface CampaignSettingsFormProps {
  settings: CampaignSettings;
  onChange: (settings: Partial<CampaignSettings>) => void;
  campaignType?: string;
}

const CampaignSettingsForm: React.FC<CampaignSettingsFormProps> = ({
  settings,
  onChange,
  campaignType
}) => {
  const form = useForm({
    defaultValues: settings
  });

  const { watch, setValue } = form;
  const watchedValues = watch();
  
  React.useEffect(() => {
    onChange(watchedValues);
  }, [watchedValues, onChange]);

  const handleSocialOptionChange = (platform: string, checked: boolean) => {
    const currentOptions = [...settings.shareOptions];
    
    if (checked && !currentOptions.includes(platform)) {
      setValue('shareOptions', [...currentOptions, platform]);
    } else if (!checked && currentOptions.includes(platform)) {
      setValue('shareOptions', currentOptions.filter(option => option !== platform));
    }
  };

  const handleCustomMessageChange = (platform: string, message: string) => {
    setValue('customMessages', {
      ...settings.customMessages,
      [platform]: message
    });
  };

  const isAffiliateProgram = campaignType === 'affiliate-program';
  const isMilestoneGiveaways = campaignType === 'milestone-giveaways';
  const isNewsletterReferral = campaignType === 'newsletter-referral';
  const isLeaderboard = campaignType === 'leaderboard';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-lg font-medium">
              {isNewsletterReferral 
                ? "Newsletter Referral Configuration" 
                : isLeaderboard 
                  ? "Leaderboard Configuration"
                  : "Widget Configuration"}
            </h3>
            <p className="text-sm text-gray-500">
              {isNewsletterReferral 
                ? "Configure how your newsletter referral program will work and appear to subscribers" 
                : isLeaderboard
                  ? "Configure how your referral leaderboard contest will appear and function"
                  : `Configure how your ${isAffiliateProgram ? 'affiliate program' : isMilestoneGiveaways ? 'milestone giveaway' : 'referral widget'} will appear to users`}
            </p>
          </div>

          <Form {...form}>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {isNewsletterReferral 
                        ? "Program Title" 
                        : isLeaderboard
                          ? "Leaderboard Title"
                          : "Widget Title"}
                    </FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        maxLength={255} 
                        placeholder={
                          isNewsletterReferral 
                            ? "Refer Our Newsletter" 
                            : isLeaderboard
                              ? "Referral Champions"
                              : "Give $20, Get $20"
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      {isNewsletterReferral 
                        ? "Main heading displayed in referral emails and widget" 
                        : isLeaderboard
                          ? "Main heading displayed on the leaderboard"
                          : `Main heading displayed in the ${isAffiliateProgram ? 'affiliate dashboard' : 'referral widget'}`}
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="subtitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {isNewsletterReferral 
                        ? "Program Subtitle" 
                        : isLeaderboard
                          ? "Leaderboard Subtitle"
                          : "Widget Subtitle"}
                    </FormLabel>
                    <FormControl>
                      <Textarea 
                        {...field} 
                        placeholder={
                          isNewsletterReferral 
                            ? "Share this newsletter with friends and earn rewards for each signup" 
                            : isLeaderboard
                              ? "Refer more friends to climb the leaderboard and win prizes"
                              : isAffiliateProgram 
                                ? "Describe the affiliate program benefits and commission structure" 
                                : "Describe the benefit for referrer and referee. Use {{referralCode}} to include the code."
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Supporting text that explains the {
                        isNewsletterReferral 
                          ? 'newsletter referral program' 
                          : isLeaderboard
                            ? 'leaderboard contest'
                            : isAffiliateProgram 
                              ? 'affiliate program' 
                              : 'referral program'
                      }
                    </FormDescription>
                  </FormItem>
                )}
              />

              {!isAffiliateProgram && (
                <>
                  <ShareOptionsSection 
                    selectedOptions={settings.shareOptions}
                    onOptionChange={handleSocialOptionChange}
                  />

                  <CustomMessagesSection
                    messages={settings.customMessages}
                    onMessageChange={handleCustomMessageChange}
                  />
                </>
              )}
              
              <FormField
                control={form.control}
                name="showAffiliatePortal"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {isAffiliateProgram 
                          ? 'Enable Affiliate Portal' 
                          : isNewsletterReferral 
                            ? 'Show Subscriber Reward Dashboard' 
                            : isLeaderboard
                              ? 'Show Leaderboard Dashboard'
                              : 'Affiliate Portal Signup'}
                      </FormLabel>
                      <FormDescription>
                        {isAffiliateProgram 
                          ? 'Provide affiliates with a dedicated dashboard to track their performance' 
                          : isNewsletterReferral 
                            ? 'Let subscribers see their referrals and rewards in a dashboard' 
                            : isLeaderboard
                              ? 'Let users track their position on the leaderboard'
                              : 'Show/hide the affiliate/refer-a-friend portal sign-up link'}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </Form>
        </div>

        <div className="space-y-6">
          {isAffiliateProgram ? (
            <AffiliateSettingsSection form={form} />
          ) : isNewsletterReferral ? (
            <>
              <NewsletterReferralSection form={form} />
              <MilestoneThresholdsSection form={form} campaignType={campaignType} />
            </>
          ) : isLeaderboard ? (
            <LeaderboardSettingsSection form={form} />
          ) : isMilestoneGiveaways ? (
            <MilestoneThresholdsSection form={form} />
          ) : (
            <>
              <RewardConfigSection form={form} />
              <AdditionalSettingsSection form={form} />
            </>
          )}
        </div>
      </div>
      
      {isAffiliateProgram && (
        <div className="pt-6">
          <p className="text-sm text-gray-500 mb-2">
            <strong>Note:</strong> Unlike standard referral campaigns, affiliate programs track commissions based on sale value rather than fixed rewards.
          </p>
        </div>
      )}

      {isNewsletterReferral && (
        <div className="pt-6">
          <p className="text-sm text-gray-500 mb-2">
            <strong>Note:</strong> For newsletter referrals to work properly, you'll need to connect your email service provider and implement tracking in your signup process.
          </p>
        </div>
      )}

      {isLeaderboard && (
        <div className="pt-6">
          <p className="text-sm text-gray-500 mb-2">
            <strong>Note:</strong> Leaderboard referral programs are effective for creating friendly competition among users and driving higher referral volumes.
          </p>
        </div>
      )}
    </div>
  );
};

export default CampaignSettingsForm;
